// useFetch — generic async data fetch hook with cancel-on-unmount.
//
// Usage:
//   const { data, loading, error } = useFetch(
//     () => fetch('/api/foo').then(r => r.json() as Promise<Foo>),
//     [dep1, dep2]
//   );
//
// The `fn` is called on mount and whenever `deps` change. A stale
// invocation is cancelled (its setState calls are swallowed) if the
// component unmounts or deps change before the promise resolves.
//
// Design constraints (set 2026-05-13):
//   - Does NOT include AbortController — callers that need abort should
//     manage their own controller in the fn closure or keep the bespoke
//     useEffect. The pattern here matches ~85 % of real call sites.
//   - Does NOT expose a `retry` function — callers that need imperative
//     re-fetch should keep a bespoke useEffect (e.g. DipSiteDetail's
//     re-lookup button).
//   - `error` is typed `unknown` intentionally. Use `instanceof Error`
//     at the call site, or use the companion `useFetchUrl<T>` if you
//     only need a simple URL fetch with string errors.
//
// Sites NOT suited for this hook (keep bespoke useEffect):
//   - Supabase realtime subscriptions
//   - Effects that also update a shared store on completion
//   - Effects with imperative refresh triggers (user-facing Retry buttons)
//   - Effects that use AbortController for in-flight cancel on keystroke

import { useState, useEffect } from 'react';

/**
 * Generic fetch hook. `fn` is called when `deps` change; result lands
 * in `data`. Loading starts `true` and goes `false` on resolve/reject.
 */
export function useFetch<T>(
  fn: () => Promise<T>,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  deps: ReadonlyArray<unknown>,
): { data: T | null; loading: boolean; error: unknown } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  // Callers pass deps explicitly; fn identity changes are irrelevant.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}

/**
 * Convenience wrapper for simple JSON URL fetches. Returns `error` as
 * a string (extracted from Error.message) for easy rendering.
 *
 * Usage:
 *   const { data, loading, error } = useFetchUrl<MyType>('/api/foo');
 *   // or with a dynamic URL:
 *   const { data } = useFetchUrl<MyType>(`/api/foo?id=${id}`);
 */
export function useFetchUrl<T>(
  url: string,
): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    fetch(url, { signal: ac.signal })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        // Defensive: some upstreams return HTTP 200 with an empty body.
        // Treat that as null (no data) rather than throwing a JSON parse error.
        const text = await r.text();
        if (!text.trim()) return null as unknown as T;
        return JSON.parse(text) as T;
      })
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [url]);

  return { data, loading, error };
}
